import { useTranslation } from 'react-i18next';
import { Button, Card, MonthDayPicker, Spinner } from '../../components/core';
import { type FormFields, type FormErrors } from './types';

interface Props {
  mode: 'add' | 'edit';
  fields: FormFields;
  errors: FormErrors;
  cropTypes: { id: string; name: string }[];
  regions: { id: string; name: string }[];
  saving: boolean;
  onSelectChange: (_e: React.ChangeEvent<{ name?: string; value: string }>) => void;
  onStartChange: (_month: number | '', _day: number | '') => void;
  onEndChange: (_month: number | '', _day: number | '') => void;
  onSave: () => void;
  onCancel: () => void;
}

export function SeasonTimesForm({
  mode,
  fields,
  errors,
  cropTypes,
  regions,
  saving,
  onSelectChange,
  onStartChange,
  onEndChange,
  onSave,
  onCancel,
}: Props) {
  const { t } = useTranslation();

  const fieldCls =
    'w-full rounded-sm border border-gray-300 px-3 py-1.5 font-body text-sm text-gray-900 ' +
    'outline-none transition-agrobook duration-agrobook ' +
    'focus:border-green-600 focus:ring-4 focus:ring-green-600/15 ' +
    'disabled:bg-gray-50 disabled:text-gray-400';

  return (
    <Card variant="form" className="mb-4">
      <h2 className="mb-4 font-display text-base font-semibold text-gray-900">
        {mode === 'add' ? t('seasonTimes.addTitle') : t('seasonTimes.editTitle')}
      </h2>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {/* Crop Type */}
        <div>
          <label className="mb-1 block font-body text-sm font-medium text-gray-700">
            {t('seasonTimes.cropType')} <span className="text-red-500">*</span>
          </label>
          <select
            name="cropTypeId"
            value={fields.cropTypeId}
            onChange={onSelectChange}
            disabled={saving || mode === 'edit'}
            className={fieldCls}
          >
            <option value="">{t('seasonTimes.selectCropType')}</option>
            {cropTypes.map(ct => (
              <option key={ct.id} value={ct.id}>{ct.name}</option>
            ))}
          </select>
          {errors.cropTypeId && (
            <p className="mt-1 font-body text-xs text-red-600">{errors.cropTypeId}</p>
          )}
        </div>

        {/* Region */}
        <div>
          <label className="mb-1 block font-body text-sm font-medium text-gray-700">
            {t('seasonTimes.region')} <span className="text-red-500">*</span>
          </label>
          <select
            name="regionId"
            value={fields.regionId}
            onChange={onSelectChange}
            disabled={saving || mode === 'edit'}
            className={fieldCls}
          >
            <option value="">{t('seasonTimes.selectRegion')}</option>
            {regions.map(r => (
              <option key={r.id} value={r.id}>{r.name}</option>
            ))}
          </select>
          {errors.regionId && (
            <p className="mt-1 font-body text-xs text-red-600">{errors.regionId}</p>
          )}
        </div>

        {/* Start Date */}
        <MonthDayPicker
          label={t('seasonTimes.startDate')}
          required
          month={fields.startMonth}
          day={fields.startDay}
          onChange={onStartChange}
          disabled={saving}
          error={errors.startDate}
        />

        {/* End Date */}
        <MonthDayPicker
          label={t('seasonTimes.endDate')}
          required
          month={fields.endMonth}
          day={fields.endDay}
          onChange={onEndChange}
          disabled={saving}
          error={errors.endDate}
        />
      </div>

      {errors.general && (
        <p className="mt-3 font-body text-sm text-red-600">{errors.general}</p>
      )}

      <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button variant="secondary" size="sm" onClick={onCancel} disabled={saving}>
          {t('seasonTimes.cancel')}
        </Button>
        <Button variant="primary" size="sm" onClick={onSave} disabled={saving}>
          {saving ? <Spinner size="sm" /> : t('seasonTimes.save')}
        </Button>
      </div>
    </Card>
  );
}
